import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Maximize2,
  Minimize2,
  Package,
  RefreshCw,
  Save,
  Share2,
  Sparkles,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { Drawer } from '@components/Drawer';
import { Button } from '@components/Button';
import { ConfirmDialog } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { DateInputBR } from '@components/DateInputBR';
import { Lightbox } from '@components/Lightbox';
import { TagInput } from '@components/TagInput';
import { MediaStatusBadge } from './MediaThumb';
import { MediaCompressDialog } from './MediaCompressDialog';
import { canCompressMedia, formatGain, LARGE_IMAGE_BYTES } from '@/utils/mediaCompression';
import { usePermissions } from '@hooks/usePermissions';
import { useSocialPost } from '@contexts/SocialPostContext';
import socialService from '@services/socialService';
import type { SocialPost } from '@/types/social';
import { formatPostTime, toBrief } from '@/utils/social';
import { StatusChip } from '@components/social/SocialBits';
import mediaService from '@services/mediaService';
import type { MediaAsset, MediaCompressionAcceptResult, MediaEvent, MediaTagCategory, ProductSearchResult } from '@/types/media';
import {
  MEDIA_AI_TYPE_LABELS,
  MEDIA_KIND_LABELS,
  MEDIA_ORIENTATION_LABELS,
  MEDIA_SOURCE_LABELS,
} from '@/types/media';
import {
  formatBytes,
  formatDateBR,
  formatDateTimeBR,
  formatDurationLong,
  formatNumberBR,
} from '@/utils/formatters';
import { chipFromAssetTag, chipsToAssignments, type TagChip } from '@/utils/mediaTags';
import { Link } from 'react-router-dom';
import landingService from '@services/landingService';
import type { LandingPage } from '@/types/landing';

const FIELD_LABELS: Record<string, string> = {
  title: 'Título',
  description: 'Descrição',
  event_id: 'Evento',
  location: 'Local',
  event_date: 'Data do evento',
  tags: 'Tags',
  products: 'Produtos',
  sku: 'SKU',
  category_id: 'Categoria',
  is_released_for_customers: 'Liberada para clientes',
  is_preferred: 'Preferencial',
};

interface ProductChip {
  sku: string;
  product_name?: string;
  source?: 'ai' | 'user';
}

interface FormState {
  title: string;
  description: string;
  eventId: string;
  location: string;
  eventDate: string;
}

const formFrom = (m: MediaAsset): FormState => ({
  title: m.title ?? '',
  description: m.description ?? '',
  eventId: m.event_id ? String(m.event_id) : '',
  location: m.location ?? '',
  eventDate: m.event_date ?? '',
});

const tagsKey = (chips: TagChip[]) =>
  chips
    .map((c) => (c.tag_id ? `#${c.tag_id}` : `${c.category_id}:${c.name.toLowerCase()}`))
    .sort()
    .join('|');

const productsKey = (list: ProductChip[]) => list.map((p) => p.sku).sort().join('|');

/** Linha da lista de definições; some quando o valor é vazio. */
const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) =>
  value === undefined || value === null || value === '' ? null : (
    <div className="grid grid-cols-[9rem_1fr] gap-2 py-1 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800 break-words">{value}</dd>
    </div>
  );

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{title}</h4>
    <dl className="divide-y divide-slate-100">{children}</dl>
  </div>
);

const withBy = (when?: string, by?: string) => (when ? `${formatDateTimeBR(when)}${by ? ` · ${by}` : ''}` : undefined);

interface MediaDetailDrawerProps {
  mediaId: number | null;
  onClose: () => void;
  /** Mídia alterada (ou `null` quando excluída) — a lista atualiza o item. */
  onChanged: (id: number, media: MediaAsset | null) => void;
  events?: MediaEvent[];
  categories?: MediaTagCategory[];
  onPrev?: () => void;
  onNext?: () => void;
}

/**
 * Detalhe de uma mídia (aberto pelo `?media=<id>` da biblioteca): pré-visualização, metadados
 * técnicos, descrição da IA, edição (título, descrição, evento, tags, produtos), sinalizadores,
 * download do original, reprocessar e excluir.
 */
export const MediaDetailDrawer: React.FC<MediaDetailDrawerProps> = ({
  mediaId,
  onClose,
  onChanged,
  events: eventsProp,
  categories,
  onPrev,
  onNext,
}) => {
  const { checkPermission } = usePermissions();
  const canEdit = checkPermission('media.edit').hasPermission;
  const canRelease = checkPermission('media.release').hasPermission;
  const canDelete = checkPermission('media.delete').hasPermission;
  const canSocialRead = checkPermission('social.read').hasPermission;
  const canSocialWrite = checkPermission('social.write').hasPermission;
  const { openPost, openNewPost } = useSocialPost();
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const canLandingRead = checkPermission('landing.read').hasPermission;
  const [landingPages, setLandingPages] = useState<LandingPage[]>([]);

  // Landing pages que usam esta mídia (endereço público no CDN).
  useEffect(() => {
    setLandingPages([]);
    if (!mediaId || !canLandingRead) return;
    let alive = true;
    landingService
      .pagesUsingMedia(mediaId)
      .then((list) => alive && setLandingPages(list))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [mediaId, canLandingRead]);

  // Redes sociais: posts que usam esta mídia.
  useEffect(() => {
    setPosts([]);
    if (!mediaId || !canSocialRead) return;
    let alive = true;
    socialService
      .listPosts({ media_id: mediaId })
      .then((list) => alive && setPosts(list))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [mediaId, canSocialRead]);

  const [media, setMedia] = useState<MediaAsset | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [events, setEvents] = useState<MediaEvent[]>(eventsProp ?? []);
  const [form, setForm] = useState<FormState>({ title: '', description: '', eventId: '', location: '', eventDate: '' });
  const [tags, setTags] = useState<TagChip[]>([]);
  const [products, setProducts] = useState<ProductChip[]>([]);
  const [saving, setSaving] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  const [previewRetried, setPreviewRetried] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [compressOpen, setCompressOpen] = useState(false);

  // Produtos: busca no catálogo.
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState<ProductSearchResult[]>([]);
  const [productSearching, setProductSearching] = useState(false);
  const productBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (eventsProp) setEvents(eventsProp);
    else mediaService.events().then(setEvents).catch(() => setEvents([]));
  }, [eventsProp]);

  const applyMedia = useCallback((m: MediaAsset) => {
    setMedia(m);
    setForm(formFrom(m));
    setTags(m.tags.map(chipFromAssetTag));
    setProducts(m.products.map((p) => ({ sku: p.sku, product_name: p.product_name, source: p.source })));
  }, []);

  useEffect(() => {
    if (!mediaId) {
      setMedia(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotice(null);
    setPreviewUrl(undefined);
    setVideoUrl(undefined);
    setPreviewRetried(false);
    mediaService
      .getById(mediaId)
      .then(async (m) => {
        if (cancelled) return;
        applyMedia(m);
        setPreviewUrl(m.preview_url ?? m.thumb_url);
        if (m.kind === 'video') {
          try {
            const u = await mediaService.getUrl(m.id, 'original');
            if (!cancelled) setVideoUrl(u.url);
          } catch {
            /* sem vídeo: fica só a imagem de capa */
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mediaId, applyMedia]);

  useEffect(() => {
    const q = productQuery.trim();
    if (q.length < 2) {
      setProductResults([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      setProductSearching(true);
      try {
        const list = await mediaService.searchProducts(q);
        if (!cancelled) setProductResults(list);
      } catch {
        if (!cancelled) setProductResults([]);
      } finally {
        if (!cancelled) setProductSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [productQuery]);

  const dirtyForm = useMemo(() => media && JSON.stringify(formFrom(media)) !== JSON.stringify(form), [media, form]);
  const dirtyTags = useMemo(() => media && tagsKey(media.tags.map(chipFromAssetTag)) !== tagsKey(tags), [media, tags]);
  const dirtyProducts = useMemo(
    () => media && productsKey(media.products) !== productsKey(products),
    [media, products],
  );
  const dirty = !!(dirtyForm || dirtyTags || dirtyProducts);

  const handlePreviewError = async () => {
    if (!media || previewRetried) return;
    setPreviewRetried(true);
    try {
      const u = await mediaService.getUrl(media.id, media.preview_url ? 'preview' : 'thumb');
      setPreviewUrl(u.url);
    } catch {
      setPreviewUrl(undefined);
    }
  };

  const save = async () => {
    if (!media) return;
    setSaving(true);
    try {
      let latest: MediaAsset | null = null;
      if (dirtyForm) {
        latest = await mediaService.update(media.id, {
          title: form.title.trim(),
          description: form.description.trim() || null,
          event_id: form.eventId ? Number(form.eventId) : null,
          location: form.location.trim() || null,
          event_date: form.eventDate || null,
        });
      }
      if (dirtyTags) latest = (await mediaService.setTags(media.id, chipsToAssignments(tags))) ?? latest;
      if (dirtyProducts) latest = (await mediaService.setProducts(media.id, products.map((p) => p.sku))) ?? latest;
      // O detalhe completo (com preview_url) sempre vem do GET.
      const fresh = await mediaService.getById(media.id).catch(() => latest);
      if (fresh) {
        applyMedia(fresh);
        onChanged(media.id, fresh);
      }
      setNotice('Alterações salvas.');
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const toggleFlag = async (flag: 'is_released_for_customers' | 'is_preferred') => {
    if (!media) return;
    setFlagBusy(true);
    const nextValue = !media[flag];
    try {
      const updated = await mediaService.setFlags(media.id, { [flag]: nextValue });
      const next = updated ? { ...media, ...updated, preview_url: media.preview_url } : { ...media, [flag]: nextValue };
      setMedia(next);
      onChanged(media.id, next);
    } catch (err) {
      setError(err);
    } finally {
      setFlagBusy(false);
    }
  };

  const download = async () => {
    if (!media) return;
    setDownloading(true);
    try {
      const u = await mediaService.getUrl(media.id, 'original', true);
      const a = document.createElement('a');
      a.href = u.url;
      a.rel = 'noopener';
      a.download = media.original_file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError(err);
    } finally {
      setDownloading(false);
    }
  };

  const reprocess = async () => {
    if (!media) return;
    setReprocessing(true);
    try {
      await mediaService.reprocess(media.id);
      const next = { ...media, status: 'pending' as const };
      setMedia(next);
      onChanged(media.id, next);
      setNotice('Reprocessamento solicitado. A mídia volta para a fila do processamento.');
    } catch (err) {
      setError(err);
    } finally {
      setReprocessing(false);
    }
  };

  // "Usar a nova" da compactação: o original mudou — recarrega o detalhe (prévia nova vem do worker).
  const onCompressed = async (res: MediaCompressionAcceptResult) => {
    if (!media) return;
    const before = media.size_bytes;
    const fresh = await mediaService.getById(media.id).catch(() => res.media);
    applyMedia(fresh);
    setPreviewUrl(fresh.preview_url ?? fresh.thumb_url ?? previewUrl);
    setPreviewRetried(false);
    onChanged(media.id, fresh);
    // Mesmo arredondamento do backend (gain_percent): para baixo.
    const gain = before > 0 ? Math.floor(((before - fresh.size_bytes) * 100) / before) : 0;
    const where = [
      res.landing_pages ? `${res.landing_pages} landing page${res.landing_pages > 1 ? 's' : ''}` : '',
      res.email_campaigns ? `${res.email_campaigns} e-mail${res.email_campaigns > 1 ? 's' : ''}` : '',
    ].filter(Boolean);
    setNotice(
      [
        `Imagem compactada: ${formatBytes(before)} → ${formatBytes(fresh.size_bytes)} (${formatGain(gain)}).`,
        'A miniatura e a prévia são refeitas em instantes.',
        where.length ? `Atualizada também em ${where.join(' e ')}.` : '',
        ...res.warnings,
      ]
        .filter(Boolean)
        .join(' '),
    );
  };

  const remove = async () => {
    if (!media) return;
    setDeleting(true);
    try {
      await mediaService.remove(media.id);
      setConfirmDelete(false);
      onChanged(media.id, null);
      onClose();
    } catch (err) {
      setConfirmDelete(false);
      setError(err);
    } finally {
      setDeleting(false);
    }
  };

  const onEventChange = (value: string) => {
    const ev = events.find((e) => String(e.id) === value);
    setForm((f) => ({
      ...f,
      eventId: value,
      location: f.location || ev?.location || '',
      eventDate: f.eventDate || ev?.event_date || '',
    }));
  };

  const addProduct = (p: ProductSearchResult) => {
    setProducts((prev) => (prev.some((x) => x.sku === p.sku) ? prev : [...prev, { sku: p.sku, product_name: p.product_name, source: 'user' }]));
    setProductQuery('');
    setProductResults([]);
  };

  const activeEvents = events.filter((e) => e.is_active || String(e.id) === form.eventId);
  const m = media;
  const isOpen = mediaId !== null;

  const headerActions = (
    <>
      {onPrev && (
        <button type="button" onClick={onPrev} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" title="Anterior">
          <ChevronLeft size={18} />
        </button>
      )}
      {onNext && (
        <button type="button" onClick={onNext} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" title="Próxima">
          <ChevronRight size={18} />
        </button>
      )}
    </>
  );

  const footer = m && (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" icon={<Download />} onClick={download} loading={downloading}>
          Baixar original
        </Button>
        {canSocialWrite && (
          <Button variant="outline" size="sm" icon={<Share2 />} onClick={() => openNewPost({ media: [toBrief(m)], title: m.title || m.original_file_name })}>
            Criar post
          </Button>
        )}
        {canEdit && canCompressMedia(m) && (
          <Button
            variant="outline"
            size="sm"
            icon={<Minimize2 />}
            onClick={() => setCompressOpen(true)}
            title="Gerar uma versão menor, comparar com a original e escolher qual fica"
          >
            Compactar{m.size_bytes > LARGE_IMAGE_BYTES ? ` (${formatBytes(m.size_bytes)})` : ''}
          </Button>
        )}
        {canEdit && (
          <Button variant="outline" size="sm" icon={<RefreshCw />} onClick={reprocess} loading={reprocessing}>
            Reprocessar
          </Button>
        )}
        {canDelete && (
          <Button variant="outline" size="sm" icon={<Trash2 />} onClick={() => setConfirmDelete(true)} className="text-red-600">
            Excluir
          </Button>
        )}
      </div>
      {canEdit && (
        <Button variant="primary" size="sm" icon={<Save />} onClick={save} loading={saving} disabled={!dirty || !form.title.trim()}>
          Salvar
        </Button>
      )}
    </div>
  );

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        title={m ? m.title || m.original_file_name : 'Mídia'}
        headerActions={headerActions}
        footer={footer}
        closeOnEsc={!lightbox && !confirmDelete && !error && !compressOpen}
      >
        {loading && !m && (
          <p className="text-slate-400 text-sm py-12 text-center">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}

        {m && (
          <div className={clsx('space-y-5', loading && 'opacity-60')}>
            {notice && (
              <div className="flex items-start justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                {notice}
                <button type="button" onClick={() => setNotice(null)} aria-label="Fechar aviso">
                  <X size={14} />
                </button>
              </div>
            )}

            {/* Pré-visualização */}
            <div className="relative rounded-lg bg-slate-900 flex items-center justify-center min-h-[200px] max-h-[55vh] overflow-hidden">
              {m.kind === 'video' && videoUrl ? (
                <video key={videoUrl} src={videoUrl} poster={previewUrl} controls preload="metadata" className="max-h-[55vh] w-full" />
              ) : previewUrl ? (
                <>
                  <img
                    src={previewUrl}
                    alt={m.title || m.original_file_name}
                    onError={handlePreviewError}
                    className="max-h-[55vh] w-full object-contain cursor-zoom-in"
                    onClick={() => setLightbox(true)}
                  />
                  <button
                    type="button"
                    onClick={() => setLightbox(true)}
                    className="absolute bottom-2 right-2 rounded-md bg-black/60 p-1.5 text-white hover:bg-black/80"
                    title="Tela cheia"
                  >
                    <Maximize2 size={16} />
                  </button>
                </>
              ) : (
                <p className="text-slate-400 text-sm">
                  {m.status === 'ready' ? 'Pré-visualização indisponível.' : 'A pré-visualização aparece depois do processamento.'}
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="badge-neutral">{MEDIA_KIND_LABELS[m.kind]}</span>
              <MediaStatusBadge status={m.status} always />
              {m.ai_type && <span className="badge-neutral">{MEDIA_AI_TYPE_LABELS[m.ai_type]}</span>}
              {m.has_people && <span className="badge-warning">Com pessoas</span>}
              {m.status_message && <span className="text-xs text-red-600">{m.status_message}</span>}
            </div>

            {/* Sinalizadores */}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={!canRelease || flagBusy}
                onClick={() => toggleFlag('is_released_for_customers')}
                className={clsx(
                  'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed',
                  m.is_released_for_customers
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-slate-300 text-slate-600 hover:bg-slate-50',
                  !canRelease && 'opacity-70',
                )}
                title={canRelease ? 'Liberar/bloquear para envio a clientes' : 'Sem permissão para alterar'}
              >
                <BadgeCheck size={16} />
                {m.is_released_for_customers ? 'Liberada para clientes' : 'Não liberada para clientes'}
              </button>
              <button
                type="button"
                disabled={!canRelease || flagBusy}
                onClick={() => toggleFlag('is_preferred')}
                className={clsx(
                  'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed',
                  m.is_preferred ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50',
                  !canRelease && 'opacity-70',
                )}
                title={canRelease ? 'Marcar/desmarcar como preferencial' : 'Sem permissão para alterar'}
              >
                <Star size={16} className={m.is_preferred ? 'fill-amber-400 text-amber-500' : ''} />
                {m.is_preferred ? 'Preferencial' : 'Não preferencial'}
              </button>
            </div>

            {/* Descrições */}
            <div className="space-y-3">
              <div>
                <label className="text-label block mb-1">Título</label>
                <input
                  className="input-field"
                  value={form.title}
                  disabled={!canEdit}
                  maxLength={300}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                />
              </div>
              {m.ai_description && (
                <div className="rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2">
                  <p className="text-xs font-semibold text-violet-700 flex items-center gap-1 mb-1">
                    <Sparkles size={12} /> Descrição da IA
                    {(m.ai_model || m.ai_processed_at) && (
                      <span className="font-normal text-violet-500">
                        · {[m.ai_model, formatDateTimeBR(m.ai_processed_at)].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{m.ai_description}</p>
                </div>
              )}
              <div>
                <label className="text-label block mb-1">Descrição (substitui a da IA quando preenchida)</label>
                <textarea
                  className="textarea-field"
                  rows={3}
                  value={form.description}
                  disabled={!canEdit}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
            </div>

            {/* Evento */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-label block mb-1">Evento</label>
                <select
                  className="select-field"
                  value={form.eventId}
                  disabled={!canEdit}
                  onChange={(e) => onEventChange(e.target.value)}
                >
                  <option value="">Sem evento</option>
                  {activeEvents.map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {ev.name}
                      {ev.event_date ? ` (${formatDateBR(ev.event_date)})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-label block mb-1">Local</label>
                <input
                  className="input-field"
                  value={form.location}
                  disabled={!canEdit}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-label block mb-1">Data do evento</label>
                <DateInputBR
                  className="input-field w-full"
                  value={form.eventDate}
                  disabled={!canEdit}
                  onChange={(v) => setForm((f) => ({ ...f, eventDate: v }))}
                />
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="text-label block mb-1">Tags</label>
              <TagInput value={tags} onChange={setTags} allowCreate={canEdit} disabled={!canEdit} grouped categories={categories} />
              <p className="text-small mt-1 flex items-center gap-1">
                <Sparkles size={10} className="text-violet-500" /> = sugerida pela IA. Ao salvar, todas passam a ser do usuário.
              </p>
            </div>

            {/* Produtos */}
            <div ref={productBoxRef}>
              <label className="text-label block mb-1">Produtos</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {products.length === 0 && <span className="text-sm text-slate-400">Nenhum produto vinculado.</span>}
                {products.map((p) => (
                  <span
                    key={p.sku}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700"
                  >
                    {p.source === 'ai' && <Sparkles size={10} className="text-violet-500" />}
                    <Package size={10} className="text-slate-400" />
                    <span className="font-mono">{p.sku}</span>
                    {p.product_name && <span className="text-slate-500">· {p.product_name}</span>}
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => setProducts((prev) => prev.filter((x) => x.sku !== p.sku))}
                        className="ml-0.5 rounded-full hover:bg-black/10 p-0.5"
                        aria-label={`Remover ${p.sku}`}
                      >
                        <X size={10} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {canEdit && (
                <div className="relative">
                  <input
                    className="input-field"
                    placeholder="Buscar produto por SKU ou nome…"
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                  />
                  {productSearching && (
                    <Loader2 size={14} className="absolute right-3 top-3 animate-spin text-slate-400" />
                  )}
                  {productResults.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto custom-scrollbar">
                      {productResults.map((p) => (
                        <button
                          key={p.sku}
                          type="button"
                          onClick={() => addProduct(p)}
                          disabled={products.some((x) => x.sku === p.sku)}
                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-emerald-50 disabled:opacity-40"
                        >
                          <span className="font-mono text-slate-800">{p.sku}</span>
                          <span className="text-slate-500 ml-2">{p.product_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {canSocialRead && posts.length > 0 && (
              <div className="border-t border-slate-200 pt-4">
                <h3 className="text-small font-semibold text-slate-700 mb-2">Usada nos posts ({posts.length})</h3>
                <ul className="space-y-1">
                  {posts.map((p) => (
                    <li key={p.id}>
                      <button type="button" onClick={() => openPost(p.id)} className="w-full flex items-center gap-2 text-left text-sm hover:bg-slate-50 rounded px-1 py-0.5">
                        <StatusChip status={p.status} />
                        <span className="flex-1 truncate text-slate-700">{p.title}</span>
                        <span className="text-xs text-slate-500">{formatPostTime(p.scheduled_at) || 'sem data'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {canLandingRead && landingPages.length > 0 && (
              <div className="border-t border-slate-200 pt-4">
                <h3 className="text-small font-semibold text-slate-700 mb-2">Usada nas landing pages ({landingPages.length})</h3>
                <ul className="space-y-1">
                  {landingPages.map((lp) => (
                    <li key={lp.id}>
                      <Link to={`/landing-pages/${lp.id}`} className="w-full flex items-center gap-2 text-left text-sm hover:bg-slate-50 rounded px-1 py-0.5">
                        <span className="flex-1 truncate text-slate-700">{lp.title}</span>
                        <span className="text-xs text-slate-500 truncate">/{lp.slug}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Metadados técnicos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4 border-t border-slate-200 pt-4">
              <Section title="Arquivo">
                <Row label="Nome original" value={m.original_file_name} />
                <Row label="Formato" value={m.extension ? m.extension.toUpperCase().replace(/^\./, '') : undefined} />
                <Row label="Content-Type" value={m.content_type} />
                <Row label="Tamanho" value={`${formatBytes(m.size_bytes)} (${m.size_bytes.toLocaleString('pt-BR')} bytes)`} />
                <Row label="Downloads/usos" value={m.usage_count.toLocaleString('pt-BR')} />
              </Section>
              <Section title={m.kind === 'video' ? 'Vídeo' : 'Imagem'}>
                <Row label="Dimensões" value={m.width && m.height ? `${m.width} × ${m.height} px` : undefined} />
                <Row label="Megapixels" value={m.megapixels ? `${formatNumberBR(m.megapixels)} MP` : undefined} />
                <Row label="Orientação" value={m.orientation ? MEDIA_ORIENTATION_LABELS[m.orientation] : undefined} />
                <Row label="DPI" value={m.dpi_x || m.dpi_y ? `${m.dpi_x ?? '—'} × ${m.dpi_y ?? '—'}` : undefined} />
                <Row label="Espaço de cor" value={m.color_space} />
                <Row label="Duração" value={formatDurationLong(m.duration_seconds)} />
                <Row label="Quadros/s" value={m.frame_rate ? `${formatNumberBR(m.frame_rate, 2)} fps` : undefined} />
                <Row label="Codec de vídeo" value={m.video_codec} />
                <Row label="Codec de áudio" value={m.audio_codec} />
                <Row label="Taxa de bits" value={m.bitrate_kbps ? `${m.bitrate_kbps.toLocaleString('pt-BR')} kbps` : undefined} />
              </Section>
              {(m.camera_make || m.camera_model) && (
                <Section title="Câmera">
                  <Row label="Fabricante" value={m.camera_make} />
                  <Row label="Modelo" value={m.camera_model} />
                </Section>
              )}
              <Section title="Datas">
                <Row label="Data do conteúdo" value={formatDateTimeBR(m.content_date)} />
                <Row label="Capturada em" value={formatDateTimeBR(m.captured_at)} />
                <Row label="Arquivo criado" value={formatDateTimeBR(m.file_created_at)} />
                <Row label="Arquivo modificado" value={formatDateTimeBR(m.file_modified_at)} />
                <Row label="Enviada em" value={withBy(m.created_at, m.created_by)} />
                <Row label="Atualizada em" value={withBy(m.updated_at, m.last_updated_by)} />
              </Section>
              <Section title="Origem">
                <Row label="Origem" value={MEDIA_SOURCE_LABELS[m.source]} />
                <Row label="Caminho" value={m.source_path} />
                <Row
                  label="Versões geradas"
                  value={
                    m.renditions && m.renditions.length > 0
                      ? m.renditions
                          .map((r) => `${r.kind} ${r.width}×${r.height} (${formatBytes(r.size_bytes)})`)
                          .join(' · ')
                      : undefined
                  }
                />
              </Section>
            </div>
          </div>
        )}
      </Drawer>

      {lightbox && previewUrl && m && (
        <Lightbox
          items={[{ url: previewUrl, kind: 'image', title: m.title || m.original_file_name }]}
          index={0}
          onIndexChange={() => undefined}
          onClose={() => setLightbox(false)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        title="Excluir mídia"
        message={`Excluir "${m?.title || m?.original_file_name || ''}"? Ela some das buscas e do CRM (o arquivo fica guardado).`}
        confirmText="Excluir"
        severity="danger"
        loading={deleting}
      />

      {m && canEdit && (
        <MediaCompressDialog media={m} isOpen={compressOpen} onClose={() => setCompressOpen(false)} onAccepted={onCompressed} />
      )}
      <ApiErrorModal error={error} onClose={() => setError(null)} fieldLabels={FIELD_LABELS} />
    </>
  );
};

export default MediaDetailDrawer;
